<template>
  <customize-page
    label-position="left"
    inline
    :loading="loading"
    :data="dataList"
    :columns="columns"
    :pagination="pagination"
    :formConfig="getFormConfig()"
    :filterObj="filterObj"
    :hideOperate="true"
    @size-change="handleSizeChange"
    @current-change="handleCurrentChange"
  >
    <div slot="footer">
      <el-button type="primary" @click="search">搜索</el-button>
      <el-button @click="reset">重置</el-button>
    </div>
  </customize-page>
</template>

<script>
import CustomizePage from "@/components/customize-page";
import Page from "@/mixin/Page";
import declareApi from "@/api/declare";
import shopAuthApi from "@/api/shopAuthApi";
import { formatDate } from "@/utils/common";
import { mapGetters, mapState } from "vuex";

const defaultRecordTimeRange = () => {
  const start = new Date();
  start.setDate(start.getDate() - 5);
  start.setHours(0, 0, 0, 0);
  const end = new Date();
  end.setHours(23, 59, 59, 999);
  return [start, end];
};

export default {
  components: { CustomizePage },
  mixins: [Page],
  data() {
    return {
      loading: false,
      dataList: [],
      shopList: [],
      filterObj: {
        recordTimeRange: defaultRecordTimeRange(),
        orderSource: "",
        actionType: "",
        subBizType: "",
        state: "",
        merchantOrderNo: "",
        originMerchantOrderNo: ""
      },
      columns: [
        {
          title: "商户订单号",
          key: "merchantOrderNo",
          width: 200,
          align: "center"
        },
        {
          title: "原始商户订单号",
          key: "originMerchantOrderNo",
          width: 200,
          align: "center"
        },
        {
          title: "订单渠道",
          key: "orderSourceDesc",
          width: 150,
          align: "center",
          render: (h, { row }) => (
            <span>{row.orderSourceDesc || row.orderSource}</span>
          )
        },
        {
          title: "订单操作节点",
          key: "actionTypeDesc",
          width: 140,
          align: "center",
          render: (h, { row }) => (
            <span>{row.actionTypeDesc || row.actionType}</span>
          )
        },
        {
          title: "取消人/操作人",
          key: "operator",
          width: 140,
          align: "center"
        },
        {
          title: "时间",
          key: "recordTime",
          width: 180,
          align: "center",
          render: (h, { row }) => <span>{formatDate(row.recordTime)}</span>
        },
        {
          title: "OMS单据状态",
          key: "omsOrderStateDesc",
          width: 140,
          align: "center",
          render: (h, { row }) => (
            <span>{row.omsOrderStateDesc || row.omsOrderState}</span>
          )
        },
        {
          title: "订单操作节点时间",
          key: "actionTime",
          width: 180,
          align: "center",
          render: (h, { row }) => <span>{formatDate(row.actionTime)}</span>
        }
      ]
    };
  },
  computed: {
    ...mapState("channelStore", { allChannelList: "allChannelList" }),
    ...mapGetters(["user"])
  },
  mounted() {
    this.fetch();
    this.fetchAllShop();
  },
  methods: {
    customRest() {
      this.filterObj.recordTimeRange = defaultRecordTimeRange();
    },
    fetchAllShop() {
      return shopAuthApi
        .listAll()
        .then(res => {
          this.shopList = (res.items || [])
            .filter(
              item =>
                !this.user ||
                !this.user.customerId ||
                item.customerId === this.user.customerId
            )
            .map(item => ({ label: item.shopName, value: item.shopCode }));
        })
        .catch(() => {
          this.$message.error("店铺列表加载失败，请刷新页面重试");
        });
    },
    fetch() {
      const params = { ...this.getFilterParams() };
      const range = params.recordTimeRange;
      delete params.recordTimeRange;
      if (range && range.length === 2) {
        params.recordTimeStart = new Date(range[0]).getTime();
        params.recordTimeEnd = new Date(range[1]).getTime();
      }
      this.loading = true;
      declareApi
        .orderActionRecordPageQuery({
          request: {
            ...params,
            currentPage: this.pagination.currentPage,
            pageSize: this.pagination.pageSize
          }
        })
        .then(res => {
          this.dataList = res.orderActionRecordItemList || [];
          this.pagination = { ...this.pagination, ...(res.pageInfo || {}) };
        })
        .finally(() => {
          this.loading = false;
        });
    },
    getFormConfig() {
      const type = this.getFormItemType();
      return [
        {
          data: [
            {
              label: "时间",
              model: "recordTimeRange",
              type: type.DATETIME_RRANGE,
              className: "flex-form-item"
            },
            {
              label: "订单渠道",
              model: "orderSource",
              type: type.SELECT,
              props: { data: this.allChannelList || [] }
            },
            {
              label: "操作节点",
              model: "actionType",
              type: type.SELECT,
              props: {
                data: [
                  { label: "创建订单", value: "CREATE_ORDER" },
                  { label: "取消订单", value: "CANCEL_ORDER" },
                  { label: "申报", value: "DECLARE" },
                  { label: "放行", value: "RELEASE" },
                  { label: "推送仓库", value: "PUSH_WAREHOUSE" },
                  { label: "分配", value: "ASSIGN" },
                  { label: "装箱", value: "PACK" },
                  { label: "出库", value: "STOCKOUT" }
                ]
              }
            },
            {
              label: "店铺",
              model: "subBizType",
              type: type.SELECT,
              props: { data: this.shopList }
            },
            {
              label: "订单状态",
              model: "state",
              type: type.SELECT,
              props: {
                data: [
                  { label: "待审核", value: "WAIT_AUDITING" },
                  { label: "待出库", value: "WAIT_STOCKOUT" },
                  { label: "出库中", value: "STOCKOUTING" },
                  { label: "已出库", value: "STOCKOUT_FINISH" },
                  { label: "已取消", value: "STOCKOUT_CANCEL" }
                ]
              }
            },
            { label: "商户订单号", model: "merchantOrderNo", type: type.INPUT },
            {
              label: "原始商户订单号",
              model: "originMerchantOrderNo",
              type: type.INPUT
            }
          ]
        }
      ];
    }
  }
};
</script>
